import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, ChevronRight, ChevronLeft, Link2 } from "lucide-react";
import { toast } from "sonner";
import { imgSrc, TELEGRAM_URL } from "@/lib/api";
import { PlatformPills } from "@/components/AppGallery";
import { SmartImage } from "@/components/SmartImage";

export const AppDetailModal = ({ app, onClose }) => {
  const gallery = [app.main_image, ...(app.images || [])].filter(
    (v, i, arr) => v && arr.indexOf(v) === i
  );
  const [active, setActive] = useState(0);
  const [touchX, setTouchX] = useState(null);
  const [zoom, setZoom] = useState(false);

  const shareUrl = `${window.location.origin}/api/share/${app.slug || app.id}`;
  const shareText = `Check out ${app.name} — rebranded with your logo by Cowboy Apps`;
  const telegramShare = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("App link copied — paste it anywhere");
    } catch {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };

  const next = () => setActive((a) => (a + 1) % gallery.length);
  const prev = () => setActive((a) => (a - 1 + gallery.length) % gallery.length);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoom(false);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, gallery.length]);

  const onTouchStart = (e) => setTouchX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next(); else prev();
    }
    setTouchX(null);
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="app-detail-modal"
        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-[#12121A] border border-[#282836] rounded-t-3xl sm:rounded-3xl"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <button
            data-testid="app-detail-close-button"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-[#08080C]/80 border border-[#282836] flex items-center justify-center text-white hover:border-[#FF5500] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-6 sm:p-10">
            <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#FF5500] mb-2">{app.tagline || "Rebranded for you"}</p>
            <h3 data-testid="app-detail-title" className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              {app.name}
            </h3>
            <PlatformPills platforms={app.platforms} appId={`modal-${app.id}`} />
            <p data-testid="app-detail-description" className="mt-5 text-[#9E9EB3] leading-relaxed">
              {app.description}
            </p>

            {gallery.length > 0 && (
              <div className="mt-8" data-testid="app-detail-gallery">
                <div
                  className="relative aspect-video overflow-hidden rounded-2xl border border-[#282836] bg-[#0C0C12] select-none cursor-zoom-in"
                  onTouchStart={onTouchStart}
                  onTouchEnd={onTouchEnd}
                  onClick={() => setZoom(true)}
                  data-testid="app-detail-viewer"
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={gallery[active] + active}
                      className="w-full h-full"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <SmartImage src={imgSrc(gallery[active])} alt={`${app.name} screenshot ${active + 1}`} phoneFrame={app.phone_frame !== false} />
                    </motion.div>
                  </AnimatePresence>
                  {gallery.length > 1 && (
                    <>
                      <button
                        data-testid="gallery-prev-button"
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#08080C]/80 border border-[#282836] flex items-center justify-center text-white hover:border-[#FF5500] transition-colors z-10"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        data-testid="gallery-next-button"
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#08080C]/80 border border-[#282836] flex items-center justify-center text-white hover:border-[#FF5500] transition-colors z-10"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <span className="absolute bottom-3 right-3 bg-[#08080C]/80 rounded-md px-2 py-1 font-mono2 text-[10px] text-[#FF5500] z-10">
                        {active + 1} / {gallery.length}
                      </span>
                    </>
                  )}
                </div>

                {gallery.length > 1 && (
                  <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
                    {gallery.map((img, i) => (
                      <button
                        key={img + i}
                        data-testid={`app-detail-thumb-${i}`}
                        onClick={() => setActive(i)}
                        className={`shrink-0 w-24 sm:w-28 aspect-video overflow-hidden rounded-lg border transition-colors ${
                          i === active ? "border-[#FF5500]" : "border-[#282836] hover:border-[#9E9EB3]"
                        }`}
                      >
                        <SmartImage src={imgSrc(img)} alt={`${app.name} thumbnail ${i + 1}`} phoneFrame={false} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                data-testid="app-detail-order-button"
                href={TELEGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-brand inline-flex items-center gap-2 bg-[#FF5500] text-[#08080C] font-semibold px-7 py-3.5 rounded-full"
              >
                <Send className="w-4 h-4" /> Order {app.name} on Telegram
              </a>
              <a
                data-testid="app-detail-share-telegram"
                href={telegramShare}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#282836] hover:border-[#FF5500] text-[#F4F4F8] px-6 py-3.5 rounded-full transition-colors"
              >
                <Send className="w-4 h-4 text-[#FF5500]" /> Share
              </a>
              <button
                data-testid="app-detail-copy-link"
                onClick={copyLink}
                className="inline-flex items-center gap-2 border border-[#282836] hover:border-[#FF5500] text-[#F4F4F8] px-6 py-3.5 rounded-full transition-colors"
              >
                <Link2 className="w-4 h-4 text-[#FF5500]" /> Copy link
              </button>
            </div>
          </div>
        </motion.div>

        <AnimatePresence>
          {zoom && gallery.length > 0 && (
            <motion.div
              data-testid="image-lightbox"
              className="fixed inset-0 z-[90] flex items-center justify-center bg-black/95"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setZoom(false)}
            >
              <button
                data-testid="lightbox-close-button"
                onClick={() => setZoom(false)}
                className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full bg-[#12121A] border border-[#282836] flex items-center justify-center text-white hover:border-[#FF5500] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              {gallery.length > 1 && (
                <>
                  <button
                    data-testid="lightbox-prev-button"
                    onClick={(e) => { e.stopPropagation(); prev(); }}
                    className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-[#12121A] border border-[#282836] flex items-center justify-center text-white hover:border-[#FF5500] transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    data-testid="lightbox-next-button"
                    onClick={(e) => { e.stopPropagation(); next(); }}
                    className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-[#12121A] border border-[#282836] flex items-center justify-center text-white hover:border-[#FF5500] transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              <motion.img
                key={gallery[active] + active}
                data-testid="lightbox-image"
                src={imgSrc(gallery[active])}
                alt={`${app.name} screenshot ${active + 1}`}
                className="max-w-[92vw] max-h-[86vh] object-contain rounded-xl"
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                draggable={false}
              />
              {gallery.length > 1 && (
                <span className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-[#12121A] border border-[#282836] rounded-md px-2.5 py-1 font-mono2 text-[10px] text-[#FF5500]">
                  {active + 1} / {gallery.length}
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};
